import React, { type AriaRole, type FC, type PropsWithChildren } from "react";
import Icon from "../../../../../shared-components/toyota/styles/atoms/Icon";
import * as Styles from "./ServicesCard.styles";
import type { UscIconVariantType } from "../../../../../shared-components/toyota/styles/atoms/Icon.types";
import Text from "../../../../../shared-components/toyota/styles/atoms/Text";
import Button from "../../../../../shared-components/toyota/styles/atoms/Button";
import { useUscLabel } from "../../../../utils/labels";

type ServicesCardType = {
    title: string | null;
    icon?: UscIconVariantType;
    optional?: boolean;
    hideOptionalIncluded?: boolean;
    descriptionLabel?: string | null;
    isActive?: boolean;
    showMoreInfoModal?: string | null;
    showMoreInfoButtonLabel?: string | null;
    showMoreInfoOnclick?: () => void;
    role?: AriaRole;
};

type ServicesCardDetailsType = {
    title: string;
    active: boolean;
    removeService?: () => void;
};

export const ServicesCardDetails: FC<PropsWithChildren<ServicesCardDetailsType>> = (props) => {
    const { title, active, removeService, children } = props;
    const closeLabel = useUscLabel("uscA11yButtonRemoveService");

    return (
        <Styles.Details active={active}>
            <Styles.DetailsHeader>
                <Text tag="h3" variant="h6">
                    {title}
                </Text>
                {removeService && (
                    <Button variant="icon-text" onClick={removeService} aria-label={closeLabel}>
                        <Icon variant="close" iconSize="small" />
                    </Button>
                )}
            </Styles.DetailsHeader>

            {children}
        </Styles.Details>
    );
};

export const ServicesCardFooter: FC<PropsWithChildren> = ({ children }) => {
    return <Styles.Footer>{children}</Styles.Footer>;
};

const ServicesCard: FC<PropsWithChildren<ServicesCardType>> = (props) => {
    const {
        title,
        icon,
        optional,
        descriptionLabel,
        isActive,
        showMoreInfoModal,
        showMoreInfoButtonLabel,
        showMoreInfoOnclick,
        hideOptionalIncluded,
        role,
        children,
    } = props;
    const [optionalLabel, includedLabel] = useUscLabel(["uscServicesOptional", "uscServicesIncluded"]);

    return (
        <Styles.Wrapper isActive={isActive} role={role}>
            <Styles.Header>
                <Styles.Title>
                    {!!icon && <Icon variant={icon} iconSize="medium" />}
                    <Text tag="h2" variant="h6">
                        {title}
                    </Text>
                </Styles.Title>
                {!hideOptionalIncluded && (
                    <Text tag="span" variant="body-small-regular" color="grey-7">
                        {optional ? optionalLabel : includedLabel}
                    </Text>
                )}
            </Styles.Header>
            <Styles.Content>
                {descriptionLabel && (
                    <Text tag="div" variant="body-book">
                        {descriptionLabel}
                    </Text>
                )}
                {showMoreInfoModal && (
                    <Button variant="link" size="small" onClick={showMoreInfoOnclick}>
                        {showMoreInfoButtonLabel}
                    </Button>
                )}
            </Styles.Content>

            {children}
        </Styles.Wrapper>
    );
};

export default ServicesCard;
